import React, { Suspense, useEffect, useState } from 'react';

import { UserState, useAuth } from '../../api/AuthProvider';
import { useDictionary } from '../../hooks/useDictionary';
import { ButtonVariant } from '../../styles/buttons';
import RichText from '../RichText/RichText';

export type AuthMessagesProps = {};

// We lazy load the Modal - We most likely won't need it
const Modal = !process.env.SERVER
  ? process.env.NODE_ENV === 'test'
    ? require('../Modal/Modal').default
    : React.lazy(() => import('../Modal/Modal'))
  : null;

/**
 * Show messages to user, relating to the authentication state.
 * Right now this is limited to session expired.
 */
function AuthMessages(props: AuthMessagesProps) {
  const { t } = useDictionary();
  const { userState, logoutUrl } = useAuth();
  const [showMessage, setShowMessage] = useState(false);
  const renderModal = (
    props: React.ComponentProps<typeof import('../Modal/Modal').default>,
  ) => {
    if (Modal === null) return null;

    if (process.env.NODE_ENV === 'test') {
      const TestModal = Modal as React.ComponentType<typeof props>;
      return <TestModal {...props} />;
    }

    return (
      <Suspense fallback={null}>
        <Modal {...props} />
      </Suspense>
    );
  };

  useEffect(() => {
    if (userState === UserState.SessionExpired) {
      setShowMessage(true);
    }
  }, [userState]);

  if (userState === UserState.SessionExpired && Modal !== null) {
    return renderModal({
      isOpen: showMessage,
      onDismiss: () => setShowMessage(false),
      title: t('errors.session_expired_title'),
      children: <RichText>{t('errors.session_expired_text')}</RichText>,
    });
  }

  if (userState === UserState.Forbidden && Modal !== null) {
    return renderModal({
      isOpen: true,
      title: t('errors.forbidden_title'),
      actions: [
        {
          label: t('ui.logout'),
          href: logoutUrl,
          variation: ButtonVariant.Primary,
        },
      ],
      children: <RichText>{t('errors.forbidden_text')}</RichText>,
    });
  }

  return null;
}

export default AuthMessages;
